import { FC, useCallback, useEffect, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons/faChevronLeft';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button, { ButtonType } from '../../../../components/Button/Button';
import MessageBlock, {
	BlockStyle,
	MessageType,
} from '../../../../components/MessageBlock/MessageBlock';
import AmountSummary from '../../../../components/AmountSummary/AmountSummary';
import {
	getPaymentOptionsData,
	getPreorderData,
	getCreate,
	getForm,
	getTravelRuleForm,
} from '../../../../redux/PreorderSlice/PreorderSelectors';
import { createOrder } from '../../../../redux/PreorderSlice/PreorderThunks';
import { RemoteStatus } from '../../../../redux/SharedTypes';
import { useAppDispatch } from '../../../../redux/Store';

import styles from './PreorderNetworkSelect.module.scss';
import { getErrorMessageOrDefault } from '../../../../helpers/errorMessageHelper/errorMessageHelper';
import TestIds, { formatTestId } from '../../../../tests/TestIds';
import Loader from '../../../../components/Loader/Loader';
import Network from '../../../../interfaces/Network';
import PreorderActions from '../../../../redux/PreorderSlice/PreorderActions';
import { PreorderStage, TravelRuleType } from '../../../../redux/PreorderSlice/PreorderSlice';
import { compareNetworks } from '../../../../helpers/currencyHelper/currencyHelper';
import { useNetworksQuery } from '../../../../query/network.query';

export const messages = defineMessages({
	payWith: {
		id: 'payments.pay_with',
		defaultMessage: 'Pay with',
	},
	button: {
		id: 'payments.proceed_to_pay',
		defaultMessage: 'Proceed to pay',
	},
	metaDescription: {
		id: 'payments.preorder_meta',
		defaultMessage: 'Pre-Order information',
	},
	networkNotAvailable: {
		id: 'payments.netowrk_not_available',
		defaultMessage: 'This network is currently unavailable.',
	},
	back: {
		id: 'payments.back',
		defaultMessage: 'Back',
	},
	selectNetwork: {
		id: 'payments.select_network',
		defaultMessage: 'Select network (blockchain)',
	},
	noNetworkAvailable: {
		id: 'payments.no_network_available',
		defaultMessage: 'No network availabe for this preorder',
	},
	paymentInfo: {
		id: 'payments.payment_info',
		defaultMessage:
			'You will have 15 minutes to make your payment. Ensure your wallet is already with sufficient amount of crypto assets.',
	},
	fee: {
		id: 'payments.fee',
		defaultMessage: 'Fee',
	},
});

const NetworkFC: FC<{ network: Network }> = ({ network }) => {
	const { formatMessage } = useIntl();
	const dispatch = useAppDispatch();
	const { network: formNetwork, payCurrency } = useSelector(getForm);
	const paymentOptions = useSelector(getPaymentOptionsData)!;

	const feeAmount = useMemo(() => {
		return paymentOptions.find(({ currencyCode }) => currencyCode === payCurrency)?.feeAmount;
	}, [payCurrency, paymentOptions]);

	const setSelected = useCallback(() => {
		if (!network.isEnabled) return;
		dispatch(PreorderActions.setNetwork(network.networkName));
	}, [network.isEnabled, network.networkName, dispatch]);

	return (
		<div
			className={classNames(styles.network, {
				[styles.disabled]: !network.isEnabled,
				[styles.selected]: network.networkName === formNetwork,
			})}
			onClick={setSelected}
			data-cy={formatTestId(TestIds.PreorderNetwork_0, network.networkName)}
			data-active={network.networkName === formNetwork}
			data-disabled={!network.isEnabled}
		>
			<div className={styles.info}>
				<div className={styles.label}>
					<div className={styles.title}>{network.publicName}</div>
					{feeAmount && (
						<div className={styles.fee}>{`${formatMessage(
							messages.fee
						)} ${feeAmount} ${payCurrency}`}</div>
					)}
				</div>
			</div>
			{!network.isEnabled && (
				<div className={styles.note}>{formatMessage(messages.networkNotAvailable)}</div>
			)}
		</div>
	);
};

const NetworksFC: FC<{ networks: Network[] }> = ({ networks }) => {
	const { formatMessage } = useIntl();

	return (
		<div className={styles.selectBlock}>
			<div className={styles.header}>{formatMessage(messages.selectNetwork)}</div>
			<div className={styles.networks}>
				{[...networks!]
					.sort((a, b) => compareNetworks(a.networkName, b.networkName))
					.map((network) => (
						<NetworkFC key={network.networkName} network={network} />
					))}
			</div>
		</div>
	);
};

const PreorderBody: FC = () => {
	const { formatMessage } = useIntl();
	const dispatch = useAppDispatch();
	const { id } = useSelector(getPreorderData)!;
	const { payCurrency, network } = useSelector(getForm)!;
	const {
		travelRuleType,
		firstName,
		lastName,
		dateOfBirth,
		countryOfResidence,
		companyName,
		companyCode,
		countryOfRegistration,
		email,
	} = useSelector(getTravelRuleForm) || {};
	const { status: createStatus, error } = useSelector(getCreate);
	const paymentOptions = useSelector(getPaymentOptionsData);
	const {
		data: networks,
		isLoading: isLoadingNetworks,
		error: networksError,
	} = useNetworksQuery(payCurrency ? { currencyCode: payCurrency } : undefined);

	const paymentCurrency = paymentOptions?.find((x) => x.currencyCode === payCurrency);

	const handleOpenOrderButtonClick = useCallback(() => {
		const privatePerson = {
			name: firstName,
			surname: lastName,
			dateOfBirth: dateOfBirth?.toLocaleDateString('en-CA'),
			countryOfResidence,
			email,
		};

		const company = {
			companyName,
			companyCode,
			countryOfRegistration,
			email,
		};

		const customer = {
			type: travelRuleType,
			...(travelRuleType === TravelRuleType.Personal ? { privatePerson } : { company }),
		};

		dispatch(
			createOrder({
				preorderId: id,
				payCurrencyCode: payCurrency!,
				network: network!,
				customer,
			})
		);
	}, [
		dispatch,
		id,
		payCurrency,
		network,
		travelRuleType,
		email,
		firstName,
		lastName,
		dateOfBirth,
		countryOfResidence,
		companyName,
		companyCode,
		countryOfRegistration,
	]);

	const onBack = useCallback(() => {
		dispatch(PreorderActions.setStage(PreorderStage.CurrencySelect));
		dispatch(PreorderActions.setNetwork(null));
	}, []);

	useEffect(() => {
		if (networks) {
			const defaultNetwork = networks.find((n) => n.isDefault);
			if (defaultNetwork) {
				dispatch(PreorderActions.setNetwork(defaultNetwork.networkName));
			}
		}
	}, [networks]);

	if (!payCurrency) return null;

	return (
		<div className={styles.body} data-cy={TestIds.Preorder}>
			{createStatus === RemoteStatus.Error && (
				<MessageBlock
					withIcon
					data-cy={TestIds.PreorderNetworkCreateError}
					style={BlockStyle.Border}
					type={MessageType.Error}
					message={formatMessage(getErrorMessageOrDefault(error))}
				/>
			)}
			{networksError && (
				<MessageBlock
					withIcon
					data-cy={TestIds.PreorderNetworkNetworksError}
					style={BlockStyle.Border}
					type={MessageType.Error}
					message={formatMessage(getErrorMessageOrDefault(networksError))}
				/>
			)}
			{isLoadingNetworks && <Loader className={styles.loader} />}
			{networks && networks.length > 0 ? (
				<>
					<div className={styles.currency}>
						<NetworksFC networks={networks} />
					</div>
					<AmountSummary
						currencyCode={paymentCurrency?.currencyCode || ''}
						orderAmount={paymentCurrency?.orderAmount || ''}
						networkFee={paymentCurrency?.feeAmount || ''}
						totalAmount={paymentCurrency?.amount || ''}
					/>
					<MessageBlock
						withIcon
						data-cy={TestIds.PreorderCurrencyPaymentInfo}
						message={formatMessage(messages.paymentInfo)}
						type={MessageType.Info}
						style={BlockStyle.Fill}
					/>
					<div className={styles.actionButtons}>
						<div className={styles.back} onClick={onBack}>
							<FontAwesomeIcon icon={faChevronLeft} scale={2} />
						</div>
						<Button
							isFullWidth
							data-cy={TestIds.PreorderNetworkConfirm}
							className={styles.button}
							onClick={handleOpenOrderButtonClick}
							text={formatMessage(messages.button)}
							type={ButtonType.BUTTON}
							isDisabled={
								createStatus === RemoteStatus.InProgress || !network || !payCurrency
							}
						/>
					</div>
				</>
			) : !isLoadingNetworks ? (
				<MessageBlock
					data-cy={TestIds.PreorderNetworkNoNetworks}
					style={BlockStyle.Fill}
					type={MessageType.Error}
					message={formatMessage(messages.noNetworkAvailable)}
				/>
			) : null}
		</div>
	);
};

export default PreorderBody;
